import constants from 'config/constants';
import Auth from 'j-toker';
import { getClient } from 'launchDarkly';
import store from 'store';

let developmentOverrides = null;

function getDevelopmentOverrides() {
  if (window.ENV != 'development') {
    return {};
  }

  if (developmentOverrides === null) {
    developmentOverrides = {};

    // Synchronous on purpose!
    // So we can have the overrides available as soon as possible, before anything else happens.
    const base_api_url = `${constants.BASE_API_URL}/v1`;
    const url = `${base_api_url}/feature_set/development_overrides`;

    const request = new XMLHttpRequest();
    request.open('GET', url, false); // 'false' makes the request synchronous
    request.send(null);

    if (request.status === 200) {
      developmentOverrides = JSON.parse(request.responseText);
    }
  }

  return developmentOverrides;
}

// Keyed by landlord rather than a single flag: featureEnabled runs during route resolution,
// before onAuthenticationSuccess seeds redux, so a one-shot latch can be spent on a boot-time
// read and permanently mask the real signal.
const reportedMissingFeatureSetFor = new Set();

// A signed-in lead should always hold a feature set -- the sign-in payload carries one and
// every page load reseeds it -- and an empty one silently reads every database-backed toggle
// as false. deliveredKeyCount separates a payload we dropped from one the server never sent,
// which is the only way to tell those apart without a console capture from the resident.
function reportMissingFeatureSet(featureSet) {
  if (Object.keys(featureSet).length > 0) {
    return;
  }

  const user = Auth.user;
  if (
    !user?.signedIn ||
    user.type !== 'lead' ||
    reportedMissingFeatureSetFor.has(user.landlord_id)
  ) {
    return;
  }

  reportedMissingFeatureSetFor.add(user.landlord_id);

  const message = '[ToggleBot] Landlord feature set missing from session';

  console.warn(message);
  window.Rollbar?.warning(message, {
    deliveredKeyCount: user.feature_set ? Object.keys(user.feature_set).length : 0,
    landlordId: user.landlord_id,
    userType: user.type,
    path: window.location.pathname,
  });
}

// Lets callers tell "every option is genuinely off" from "we could not resolve the toggles".
export function hasFeatureSet() {
  return Object.keys(store.getState().userTypes.featureSet).length > 0;
}

export function featureEnabledInDatabase(featureSet, key) {
  if (featureSet[key] && featureSet[key]['enabled'] === true) {
    return true;
  }
  return false;
}

export function featureEnabledInLaunchDarkly(key, defaultValue = false) {
  const client = getClient();

  if (!client) {
    console.warn('[LaunchDarkly]', 'Client not initialized while trying to read a feature flag');
    return defaultValue;
  }

  return client.variation(key, defaultValue);
}

// Checks if the feature is enabled in Launch Darkly
// It uses the feature from featureSet within redux as a default value
// Default to false if key does not exist on neither both.
export function featureEnabled(key) {
  const { featureSet } = store.getState().userTypes;

  reportMissingFeatureSet(featureSet);

  if (window.ENV == 'development') {
    const overrides = getDevelopmentOverrides();
    if (key in overrides) {
      return !!overrides[key];
    }
  }

  return featureEnabledInLaunchDarkly(key, featureEnabledInDatabase(featureSet, key));
}
